import type { Session } from "@/features/auth/api/session.api";

/**
 * Permission checks, in the client.
 *
 * HARD RULE: the client only HIDES. It never decides authorization. Every action hidden here
 * is also refused by the server, and every route missing from the menu still returns 404 or
 * 403 if somebody types the URL. This layer exists so the interface does not offer things that
 * will fail — not as a security boundary, because a security boundary that runs on the user's
 * machine is not one.
 */

export type PermissionKey = string;

export interface Grant {
  key: string;
  recordPolicy: "ANY" | "SELF";
  legalEntityIds: string[];
  instanceWide: boolean;
  /**
   * Every grant of this key comes from an administrative role (SYSTEM_ADMIN, PLATFORM_ADMIN — 20 October
   * 2026). Optional: a session from before the field reads as a business grant.
   */
  adminRoleOnly?: boolean | undefined;
}

export interface PermissionSet {
  /** Does the caller hold this key at all, in any scope? */
  can: (permission: PermissionKey) => boolean;
  /** Every one of them. Use for an action that genuinely needs two capabilities. */
  canAll: (permissions: readonly PermissionKey[]) => boolean;
  /**
   * Does the caller hold this key for OTHER PEOPLE's records — `recordPolicy: "ANY"`?
   *
   * This is the distinction `can` cannot make, and leaving it out was a real defect. The
   * `EMPLOYEE` role holds `hr.leave.read` with `SELF`, so a warehouse operator passed a plain
   * `can("hr.leave.read")` check — and the İK panel, whose whole subject is OTHER people's
   * leave, appeared in their menu. The server still narrowed every row to their own record,
   * so nothing leaked; what leaked was the impression that they had an HR role.
   *
   * A screen about other people asks THIS question. A screen about your own record asks `can`.
   */
  canForOthers: (permission: PermissionKey) => boolean;
  /** Every one of them, each for other people's records. */
  canAllForOthers: (permissions: readonly PermissionKey[]) => boolean;
  /**
   * Is this key limited to the caller's own record?
   *
   * The client uses it to decide whether to show a "which employee" control at all. A
   * self-scoped screen must not ASK the question — an interface that asks it makes the answer
   * look changeable, and the URL then invites the attempt. The server refuses it either way;
   * not asking is what keeps the screen honest.
   */
  isSelfScoped: (permission: PermissionKey) => boolean;
  /** Legal entities this key reaches. Empty with `instanceWide` means all of them. */
  scopeOf: (permission: PermissionKey) => Grant | null;
  isModuleEnabled: (moduleKey: string) => boolean;
}

const DENY_ALL: PermissionSet = {
  can: () => false,
  canAll: () => false,
  canForOthers: () => false,
  canAllForOthers: () => false,
  isSelfScoped: () => false,
  scopeOf: () => null,
  // Fail closed. An unauthenticated shell shows nothing rather than everything.
  isModuleEnabled: () => false,
};

export function permissionSetFrom(session: Session | undefined): PermissionSet {
  if (!session) return DENY_ALL;

  const grants = new Map<string, Grant>(
    (session.permissions as Grant[]).map((grant) => [grant.key, grant]),
  );
  const modules = new Set(session.modules);

  const forOthers = (permission: string) => grants.get(permission)?.recordPolicy === "ANY";

  return {
    can: (permission) => grants.has(permission),
    canAll: (permissions) => permissions.every((permission) => grants.has(permission)),
    canForOthers: forOthers,
    canAllForOthers: (permissions) => permissions.every(forOthers),
    isSelfScoped: (permission) => grants.get(permission)?.recordPolicy === "SELF",
    scopeOf: (permission) => grants.get(permission) ?? null,
    /**
     * `core.*` is always on — the catalogue says so and the server cannot disable it — so a
     * missing row is not a reason to hide a core surface. Anything else absent is switched off
     * or outside the customer's package — the server builds the list from both
     * (`BACKEND_CONTRACT.md` §7c) — and either way the route 404s.
     */
    isModuleEnabled: (moduleKey) => moduleKey.startsWith("core.") || modules.has(moduleKey),
  };
}

/**
 * Is the caller a BUSINESS decider for this key — `canForOthers`, and not only through an administrative
 * role (20 October 2026, user decision: «sistem yöneticisine bu tarz onaylar düşmemesi lazım»)?
 *
 * SYSTEM_ADMIN holds every key, so «onay bekleyen kongreler» landed in the system administrator's Panelim,
 * approval centre and badge. The administrator can still act when they open the record on purpose; queues
 * and counters of other people's decisions ask THIS question. A standalone function over `scopeOf`, not a
 * new `PermissionSet` member: the many hand-built permission sets in tests stay valid.
 */
export function decidesForOthers(permissions: PermissionSet, permission: PermissionKey): boolean {
  return permissions.canForOthers(permission) && permissions.scopeOf(permission)?.adminRoleOnly !== true;
}

/**
 * Does this key reach the company — the whole instance, or this company by name — and not only a unit, site or warehouse in it?
 *
 * 3 November 2026 (user decision: other unit heads do not see it). A director holds `MANAGER` scoped to their unit, so
 * `canForOthers` is true while the company-level records (fleet, congress) come back empty from the server (`legalEntityReach`).
 * The company summary then drew «Kongre bütçesi — ödenen 0 TL» and «Yolda olmayan araç 0 / 0». A card about a company-level record
 * asks THIS question. A standalone function over `scopeOf`, like `decidesForOthers`; a hand-built test set (`scopeOf` null) reaches.
 */
export function readsCompany(permissions: PermissionSet, permission: PermissionKey, legalEntityId: string | null): boolean {
  if (!permissions.canForOthers(permission)) return false;
  const grant = permissions.scopeOf(permission);
  if (!grant || grant.instanceWide) return true;
  return legalEntityId === null ? grant.legalEntityIds.length > 0 : grant.legalEntityIds.includes(legalEntityId);
}
